// Mint an Airbridge `https://abr.ge/...` short link for an invite. Falls back
// to the raw URL on any backend / API failure so the share flow never breaks.
//
// Used by every invite share site:
//   host_to_guest → HostActiveQRPage / HostGenerateQRPage
//   venue_staff   → VenueStaffPage / AdminMembersPage
//
// The backend caches results by (invite_type, invite_key) so re-shares of the
// same invite don't burn Airbridge's 50 req/s budget.
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * @param {Object} args
 * @param {'host_to_guest'|'venue_staff'} args.inviteType
 * @param {string} args.inviteKey  invite.id for host invites, invite_token for staff invites
 * @param {string} args.rawUrl     canonical web URL the user would have shared anyway
 * @param {string} [args.ogTitle]
 * @param {string} [args.ogDescription]
 * @param {string} [args.ogImageUrl]
 * @returns {Promise<string>} short URL when the mint succeeds, otherwise the raw URL.
 */
export async function mintShareLink({
  inviteType,
  inviteKey,
  rawUrl,
  ogTitle,
  ogDescription,
  ogImageUrl,
}) {
  if (!inviteType || !inviteKey || !rawUrl) return rawUrl;
  try {
    const res = await axios.post(
      `${API}/share-links/mint`,
      {
        invite_type: inviteType,
        invite_key: inviteKey,
        raw_url: rawUrl,
        og_title: ogTitle || null,
        og_description: ogDescription || null,
        og_image_url: ogImageUrl || null,
      },
      { headers: getAuthHeaders() },
    );
    return res.data?.short_url || rawUrl;
  } catch {
    // Never block the share flow on attribution failure.
    return rawUrl;
  }
}
